import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FormControl, InputLabel, Select, MenuItem, Button, TextField, Typography, useTheme, Box,
} from '@mui/material';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import useReportStyles from '../common/useReportStyles';
import SplitButton from '../../common/components/SplitButton';
import SelectField from '../../common/components/SelectField';
import { useRestriction } from '../../common/util/permissions';
import { ExportButton } from '../../common/components/premium';
// LocalizedDateTimeField removido - usando TextField nativo para evitar bug de navegação do calendário
import '../common/filterStyles.css';

export const updateReportParams = (searchParams, setSearchParams, key, values) => {
  const newParams = new URLSearchParams(searchParams);
  newParams.delete(key);
  newParams.delete('from');
  newParams.delete('to');
  values.forEach((value) => newParams.append(key, value));
  setSearchParams(newParams, { replace: true });
};

const ReportFilter = ({
  children, onShow, onExport, onSchedule, onPrint, deviceType = 'none',
  loading, canRun = true, hasResults,
  includePeriod = true, periodValue, onPeriodChange,
  // Layout compacto para sidebars estreitas (ReplayPage, etc)
  compact = false,
  // Premium Export Props
  exportData, exportColumns, exportTitle, exportFilename,
}) => {
  const { classes } = useReportStyles();
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Premium input styles using tokens
  const formControlSx = {
    minWidth: 240,
    '& .MuiOutlinedInput-root': {
      backgroundColor: tokens.surface.elevated,
      borderRadius: tokens.radius.md,
      transition: tokens.transitions.all,
      '& fieldset': { borderColor: tokens.border.default },
      '&:hover fieldset': { borderColor: tokens.accent.primary },
      '&.Mui-focused fieldset': { borderColor: tokens.accent.primary, borderWidth: 2 },
    },
    '& .MuiInputLabel-root': {
      color: tokens.text.secondary,
      '&.Mui-focused': { color: tokens.accent.primary },
    },
  };
  const selectSx = { backgroundColor: tokens.surface.elevated };

  // Estilos para campos datetime-local com fix do ícone do calendário
  const dateTimeFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: tokens.surface.elevated,
      borderRadius: tokens.radius.md,
      transition: tokens.transitions.all,
      '& fieldset': { borderColor: tokens.border.default },
      '&:hover fieldset': { borderColor: tokens.accent.primary },
      '&.Mui-focused fieldset': { borderColor: tokens.accent.primary, borderWidth: 2 },
    },
    '& .MuiInputLabel-root': {
      color: tokens.text.secondary,
      '&.Mui-focused': { color: tokens.accent.primary },
    },
    '& input[type="datetime-local"]': {
      colorScheme: isDarkMode ? 'dark' : 'light',
    },
    // Fix do ícone do calendário para dark theme
    '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
      cursor: 'pointer',
      filter: isDarkMode ? 'invert(1)' : 'none',
      opacity: isDarkMode ? 0.8 : 1,
    },
  };

  const [searchParams, setSearchParams] = useSearchParams();

  const readonly = useRestriction('readonly');

  const devices = useSelector((state) => state.devices.items);
  const groups = useSelector((state) => state.groups.items);

  const deviceIds = useMemo(() => searchParams.getAll('deviceId').map(Number), [searchParams]);
  const groupIds = useMemo(() => searchParams.getAll('groupId').map(Number), [searchParams]);
  const from = searchParams.get('from');
  const to = searchParams.get('to');
  const [period, setPeriod] = useState(periodValue ?? 'today');
  const [customFrom, setCustomFrom] = useState(dayjs().subtract(1, 'hour').locale('en').format('YYYY-MM-DDTHH:mm'));
  const [customTo, setCustomTo] = useState(dayjs().locale('en').format('YYYY-MM-DDTHH:mm'));
  const [selectedOption, setSelectedOption] = useState('json');

  const [description, setDescription] = useState();
  const [calendarId, setCalendarId] = useState();

  useEffect(() => {
    if (periodValue && periodValue !== period) {
      setPeriod(periodValue);
    }
  }, [periodValue]);

  const evaluateDisabled = () => {
    if (deviceType !== 'none' && !deviceIds.length && !groupIds.length) {
      return true;
    }
    if (selectedOption === 'schedule' && (!description || !calendarId)) {
      return true;
    }
    if (!canRun) {
      return true;
    }
    return loading;
  }
  const disabled = evaluateDisabled();
  const loaded = from && to && !loading;
  const resultsLoaded = typeof hasResults === 'boolean' ? hasResults : loaded;

  const evaluateOptions = () => {
    const result = {
      json: t('reportShow'),
    };
    if (onExport && resultsLoaded) {
      result.export = t('reportExport');
    }
    if ((onPrint || onExport) && resultsLoaded) {
      // added print handler
      result.print = t('reportPrint');
    }
    if (onSchedule && !readonly) {
      result.schedule = t('reportSchedule');
    }
    return result;
  }
  const options = evaluateOptions();

  useEffect(() => {
    if (from && to) {
      onShow({ deviceIds, groupIds, from, to });
    }
  }, [deviceIds, groupIds, from, to]);

  const showReport = () => {
    if (!includePeriod) {
      onShow({ deviceIds, groupIds, from, to });
      return;
    }
    let selectedFrom;
    let selectedTo;
    switch (period) {
      case 'today':
        selectedFrom = dayjs().startOf('day');
        selectedTo = dayjs().endOf('day');
        break;
      case 'yesterday':
        selectedFrom = dayjs().subtract(1, 'day').startOf('day');
        selectedTo = dayjs().subtract(1, 'day').endOf('day');
        break;
      case 'thisWeek':
        selectedFrom = dayjs().startOf('week');
        selectedTo = dayjs().endOf('week');
        break;
      case 'previousWeek':
        selectedFrom = dayjs().subtract(1, 'week').startOf('week');
        selectedTo = dayjs().subtract(1, 'week').endOf('week');
        break;
      case 'thisMonth':
        selectedFrom = dayjs().startOf('month');
        selectedTo = dayjs().endOf('month');
        break;
      case 'previousMonth':
        selectedFrom = dayjs().subtract(1, 'month').startOf('month');
        selectedTo = dayjs().subtract(1, 'month').endOf('month');
        break;
      default:
        selectedFrom = dayjs(customFrom, 'YYYY-MM-DDTHH:mm');
        selectedTo = dayjs(customTo, 'YYYY-MM-DDTHH:mm');
        break;
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.set('from', selectedFrom.toISOString());
    newParams.set('to', selectedTo.toISOString());
    setSearchParams(newParams, { replace: true });
  };

  const onSelected = (type) => {
    switch (type) {
      case 'export':
        if (onExport) {
          onExport({ deviceIds, groupIds, from, to });
        }
        break;
      case 'print':
        if (onPrint) {
          onPrint({ deviceIds, groupIds, from, to });
        } else {
          window.print();
        }
        break;
      default:
        setSelectedOption(type);
        break;
    }
  }

  const onClick = (type) => {
    switch (type) {
      case 'schedule':
        onSchedule(deviceIds, groupIds, {
          description,
          calendarId,
          attributes: {},
        });
        break;
      case 'json':
      default:
        showReport();
        break;
    }
  };

  return (
    <div className={compact ? classes.filterCompact : classes.filter}>
      {deviceType !== 'none' && (
        <div className={classes.filterItem}>
          <SelectField
            label={t(deviceType === 'multiple' ? 'deviceTitle' : 'reportDevice')}
            data={Object.values(devices).sort((a, b) => a.name.localeCompare(b.name))}
            value={deviceType === 'multiple' ? deviceIds : deviceIds.find(() => true)}
            onChange={(e) => {
              const values = deviceType === 'multiple' ? e.target.value : [e.target.value].filter((id) => id);
              updateReportParams(searchParams, setSearchParams, 'deviceId', values);
            }}
            multiple={deviceType === 'multiple'}
            fullWidth
          />
        </div>
      )}
      {deviceType === 'multiple' && (
        <div className={classes.filterItem}>
          <SelectField
            label={t('settingsGroups')}
            data={Object.values(groups).sort((a, b) => a.name.localeCompare(b.name))}
            value={groupIds}
            onChange={(e) => {
              const values = e.target.value;
              updateReportParams(searchParams, setSearchParams, 'groupId', values);
            }}
            multiple
            fullWidth
          />
        </div>
      )}
      {selectedOption !== 'schedule' ? (
        <>
          {includePeriod && (
            <>
              <div className={period === 'custom' ? classes.filterItemMedium : classes.filterItem}>
                <FormControl fullWidth size="small" variant="outlined" sx={formControlSx}>
                  <InputLabel>{t('reportPeriod')}</InputLabel>
                  <Select
                    label={t('reportPeriod')}
                    value={period}
                    onChange={(e) => {
                      setPeriod(e.target.value);
                      if (onPeriodChange) {
                        onPeriodChange(e.target.value);
                      }
                    }}
                    size="small"
                    sx={selectSx}
                  >
                    <MenuItem value="today">{t('reportToday')}</MenuItem>
                    <MenuItem value="yesterday">{t('reportYesterday')}</MenuItem>
                    <MenuItem value="thisWeek">{t('reportThisWeek')}</MenuItem>
                    <MenuItem value="previousWeek">{t('reportPreviousWeek')}</MenuItem>
                    <MenuItem value="thisMonth">{t('reportThisMonth')}</MenuItem>
                    <MenuItem value="previousMonth">{t('reportPreviousMonth')}</MenuItem>
                    <MenuItem value="custom">{t('reportCustom')}</MenuItem>
                  </Select>
                </FormControl>
              </div>
              {period === 'custom' && (
                <div className={`${classes.filterItemDateTime} ${isDarkMode ? 'datetime-dark' : ''}`}>
                  <TextField
                    label={t('reportFrom')}
                    type="datetime-local"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { colorScheme: isDarkMode ? 'dark' : 'light' } }}
                    sx={dateTimeFieldSx}
                  />
                </div>
              )}
              {period === 'custom' && (
                <div className={`${classes.filterItemDateTime} ${isDarkMode ? 'datetime-dark' : ''}`}>
                  <TextField
                    label={t('reportTo')}
                    type="datetime-local"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { colorScheme: isDarkMode ? 'dark' : 'light' } }}
                    sx={dateTimeFieldSx}
                  />
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          <div className={classes.filterItem}>
            <TextField
              value={description || ''}
              onChange={(event) => setDescription(event.target.value)}
              label={t('sharedDescription')}
              fullWidth
            />
          </div>
          <div className={classes.filterItem}>
            <SelectField
              value={calendarId}
              onChange={(event) => setCalendarId(Number(event.target.value))}
              endpoint="/api/calendars"
              label={t('sharedCalendar')}
              fullWidth
            />
          </div>
        </>
      )}
      {children}
      <div className={classes.filterItemButton}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
          {Object.keys(options).length === 1 ? (
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              disabled={disabled}
              onClick={onClick}
            >
              <Typography variant="button" noWrap>{t(loading ? 'sharedLoading' : 'reportShow')}</Typography>
            </Button>
          ) : (
            <SplitButton
              fullWidth
              variant="outlined"
              color="secondary"
              disabled={disabled}
              onClick={onClick}
              selected={selectedOption}
              setSelected={onSelected}
              options={options}
            />
          )}
          {/* Premium Export Button - shows when data is available */}
          {exportData && exportData.length > 0 && exportColumns && (
            <ExportButton
              data={exportData}
              columns={exportColumns}
              title={exportTitle || t('reportTitle')}
              filename={exportFilename || 'report'}
              size="small"
              variant="icon"
            />
          )}
        </Box>
      </div>
    </div>
  );
};

export default ReportFilter;
